import { useState } from "react";
import { Box, Button } from "@mui/material";

import IdentificationSection from "./IdentificationSection";
import ClassificationSection from "./ClassificationSection";
import SupplierCostSection from "./SupplierCostSection";
import PricingSection from "./PricingSection";
import ExpertRatingsSection from "./ExpertRatingsSection";
import DescriptionImageSection from "./DescriptionImageSection";
import PublishingSection from "./PublishingSection";
import ProductLimitsSection from "./ProductLimitsSection";

const ProductNewEditForm = ({
  cin7Product,
  shopifyProduct,
  shopifyB2bProduct,
  brands,
  categories,
  suppliers,
  unitsOfMeasure,
  values,
  setValues,
  isEditing,
  isSubmitting,
  handleSave,
  handleDismiss,
}) => {
  const [error, setError] = useState(false);

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  const isPricingRowValid = (tier) => {
    const isCalculated = values[`calculated${tier}`];

    if (isCalculated) {
      return (
        !!values[`markupType${tier}`] &&
        !!values[`usePriceType${tier}`] &&
        !!values[`markupValue${tier}`]
      );
    } else {
      return values[`price${tier}`] !== "";
    }
  };

  const isPricingValid = () => {
    return isPricingRowValid(1) && isPricingRowValid(2) && isPricingRowValid(3);
  };

  const validateForm = () => {
    const {
      sku,
      title,
      status,
      shopifyStatus,
      unitOfMeasure,
      category,
      weight,
      supplier,
      latestPrice,
      fixedPrice,
      brand,
      unitsPerCase,
    } = values;

    if (
      !sku ||
      !title ||
      !status ||
      !shopifyStatus ||
      !unitOfMeasure ||
      !values.metafields?.container_deposit_sku
    ) {
      setError(true);
      scrollToSection("identification-section");
      return false;
    }

    if (!category || !weight) {
      setError(true);
      scrollToSection("classification-section");
      return false;
    }

    if (!supplier || latestPrice === "" || fixedPrice === "" || !brand || !unitsPerCase) {
      setError(true);
      scrollToSection("supplier-cost-section");
      return false;
    }

    if (!isPricingValid()) {
      setError(true);
      scrollToSection("pricing-section");
      return false;
    }

    setError(false);
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    handleSave();
  };

  const handleDiscard = () => {
    setError(false);
    scrollToSection("identification-section");
    handleDismiss();
  }

  return (
    <Box>
      <div id="identification-section" />
      <IdentificationSection
        isEditing={isEditing}
        cin7Product={cin7Product}
        shopifyProduct={shopifyProduct}
        shopifyB2bProduct={shopifyB2bProduct}
        values={values}
        setValues={setValues}
        error={error}
        unitsOfMeasure={unitsOfMeasure}
        handleDismiss={handleDismiss}
      />
      <div id="classification-section" />
      <ClassificationSection
        isEditing={isEditing}
        cin7Product={cin7Product}
        shopifyProduct={shopifyProduct}
        values={values}
        setValues={setValues}
        categories={categories}
        error={error}
      />
      <div id="supplier-cost-section" />
      <SupplierCostSection
        isEditing={isEditing}
        cin7Product={cin7Product}
        shopifyProduct={shopifyProduct}
        values={values}
        setValues={setValues}
        suppliers={suppliers}
        brands={brands}
        error={error}
      />
      <div id="pricing-section" />
      <PricingSection
        values={values}
        setValues={setValues}
        cin7Product={cin7Product}
      />
      <div id="expert-ratings-section" />
      <ExpertRatingsSection
        values={values}
        setValues={setValues}
        showNotes
        sectionId="expert-ratings-section"
      />
      <div id="description-image-section" />
      <DescriptionImageSection
        values={values}
        setValues={setValues}
        sectionId="description-image-section"
      />
      <div id="publishing-section" />
      <PublishingSection
        values={values}
        setValues={setValues}
        isEditing={isEditing}
        sectionId="publishing-section"
      />
      <div id="product-limits-section" />
      <ProductLimitsSection
        values={values}
        setValues={setValues}
        sectionId="product-limits-section"
      />
      <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleSubmit}
          loading={isSubmitting}
        >
          Save Changes
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          fullWidth
          onClick={handleDiscard}
        >
          Dismiss
        </Button>
      </Box>
    </Box>
  );
};

export default ProductNewEditForm;
